import type { Ssm5ConnectErrorCode, Ssm5ConnectPhase, Ssm5RecentConnection, Ssm5Session } from '@packages/domain/ssm5';
import type { EnrichedVehicleDetails } from '@web/vin-sessions/types';

import { createContext } from 'react';

/** A connect attempt that failed. */
export type Ssm5ConnectFailure = {
    /** The Cloud Connector ID the attempt was made with. */
    cloudConnectorId: string;
    /** Why it failed. */
    errorCode: Ssm5ConnectErrorCode;
};

/** The SSM5 connection with its transitions. */
export type Ssm5Connection = {
    /** Drop an attempt that is still in flight or awaiting the technician's decision. */
    cancel: () => void;
    /** Open a session through the Cloud Connector and match the vehicle it reads. */
    connect: (cloudConnectorId: string) => Promise<void>;
    /** Accept the matched vehicle: the session goes on inside the vehicle's VIN session. */
    continueToVinSession: () => void;
    /** Let the attempt that is taking long run on, until it settles or runs that long again. */
    keepWaiting: () => void;
    /** The session that is live right now, or undefined without one. */
    liveConnection: Ssm5LiveConnection | undefined;
    /** Leave the lost session behind and offer a new connection. */
    restart: () => void;
    /** Drop the attempt that is taking long and start a new one through the same Cloud Connector ID. */
    restartAttempt: () => void;
    /** Show the given session again, without opening a second one. */
    resume: (connection: Ssm5LiveConnection) => void;
    /** The connection state. */
    state: Ssm5ConnectionState;
};

/** State of the SSM5 connection. */
export type Ssm5ConnectionState =
    | ({
          /** The session is live inside the vehicle's VIN session. */
          status: 'connected';
      } & Ssm5VehicleMatch)
    | ({
          /** The session was lost after pairing; its last reading stays reviewable, nothing is current. */
          status: 'disconnected';
      } & Ssm5VehicleMatch)
    | ({
          /** The vehicle is matched; the technician decides whether to open its VIN session. */
          status: 'vehicleMatched';
      } & Ssm5VehicleMatch)
    | {
          /** An attempt is in flight. */
          status: 'connecting';
          /** The Cloud Connector ID the attempt is made with. */
          cloudConnectorId: string;
          /** Whether the attempt ran longer than expected and awaits the technician's decision. */
          isTakingLong: boolean;
          /** The phase the attempt is in. */
          phase: Ssm5ConnectPhase;
          /** How far the attempt is, from 0 to 100, as the service reports it; starts over when matching the vehicle. */
          progress: number;
      }
    | ({
          /** The attempt failed; the connect card explains it while its ID is still in the field. */
          status: 'failed';
      } & Ssm5ConnectFailure)
    | {
          /** No attempt is in flight or established. */
          status: 'idle';
      };

/**
 * The SSM5 session that is live right now, kept while the technician works in other sections. It
 * reads as a recent connection with its vehicle already looked up, so the recent connections
 * section can offer it back without another lookup.
 */
export type Ssm5LiveConnection = Ssm5RecentConnection & {
    /** The TechShare vehicle record the session runs on. */
    vehicle: EnrichedVehicleDetails;
};

/** The TechShare vehicle matched to the VIN an SSM5 session read. */
export type Ssm5VehicleMatch = {
    /** The SSM5 session on the vehicle. */
    session: Ssm5Session;
    /** The TechShare vehicle record for the session VIN. */
    vehicle: EnrichedVehicleDetails;
};

export const Ssm5ConnectionContext = createContext<Ssm5Connection | undefined>(undefined);
